// Value formatting for viz (metric now; tables/charts later). TS-side per the
// port plan: currency / percent / compact / delta, chosen by an explicit
// `format=` spec or, failing that, a column-name heuristic.

export type FormatName =
  | "currency"
  | "money"
  | "number"
  | "int"
  | "percent"
  | "pct"
  | "ratio"
  | "compact"
  | "delta"
  | "text";

export function fmtCurrency(n: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: Math.abs(n) >= 1000 ? 0 : 2,
  }).format(n);
}

export function fmtNumber(n: number): string {
  return new Intl.NumberFormat("en-US").format(n);
}

export function fmtCompact(n: number): string {
  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(n);
}

/** Value is already a percentage: 50.2 -> "50.2%". */
export function fmtPercent(n: number, digits = 1): string {
  return `${n.toFixed(digits)}%`;
}

/** Value is a 0..1 ratio: 0.502 -> "50.2%". */
export function fmtRatio(n: number, digits = 1): string {
  return `${(n * 100).toFixed(digits)}%`;
}

/** Signed percentage change: 0.152 -> "+15.2%". */
export function fmtDelta(n: number): string {
  return `${n > 0 ? "+" : ""}${(n * 100).toFixed(1)}%`;
}

const FORMATTERS: Record<string, (n: number) => string> = {
  currency: fmtCurrency,
  money: fmtCurrency,
  number: fmtNumber,
  int: fmtNumber,
  percent: (n) => fmtPercent(n),
  pct: (n) => fmtPercent(n),
  ratio: (n) => fmtRatio(n),
  compact: fmtCompact,
  delta: fmtDelta,
};

/**
 * Guess a column's format from its name (the source's `fmtMetricValue`
 * heuristic, generalised). Percent checks come first so `gp_pct` beats `gp`.
 */
export function formatForName(name: string): FormatName {
  const n = name.toLowerCase();
  if (/(_pct|_rate|_margin)\b/.test(n) || /\bmargin\b/.test(n)) return "percent";
  if (/(revenue|sales|cost|spend|aov|price|gp\b|ltv|profit|amount)/.test(n))
    return "currency";
  return "number";
}

/**
 * Format one value with an explicit format name (or null to apply nothing
 * special). Non-numeric values pass through as text; null/undefined -> "—".
 */
export function formatValue(value: unknown, fmt: FormatName | null): string {
  if (value == null || value === "") return "—";
  if (fmt === "text") return String(value);
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value); // dates, labels, etc.
  const f = fmt ? FORMATTERS[fmt] : undefined;
  return f ? f(n) : fmtNumber(n);
}

/** Short label for a bar, chart point, or axis tick: "$78K", "51.3%", "1.2K". */
export function compactLabel(value: number, fmt: FormatName): string {
  if (fmt === "percent" || fmt === "pct") return fmtPercent(value);
  if (fmt === "ratio") return fmtRatio(value);
  // Sign goes outside the symbol: "-$50K", not "$-50K".
  if (fmt === "currency" || fmt === "money")
    return `${value < 0 ? "-" : ""}$${fmtCompact(Math.abs(value))}`;
  return fmtCompact(value);
}

/**
 * Parse a per-column attr — `attr="col:value, col2:value2"` — into a lookup.
 * Splits on the FIRST colon only, so values may contain colons; a part with no
 * colon is skipped rather than guessed at. Column order is the SQL's, so this
 * form is order-independent, which positional lists are not.
 */
export function parseColumnSpec(
  spec: string | undefined,
): Record<string, string> {
  const out: Record<string, string> = {};
  if (!spec) return out;
  for (const part of spec.split(",")) {
    const at = part.indexOf(":");
    if (at < 0) continue;
    const col = part.slice(0, at).trim();
    const value = part.slice(at + 1).trim();
    if (col && value) out[col] = value;
  }
  return out;
}

function isFormatName(s: string): s is FormatName {
  return s === "text" || s in FORMATTERS;
}

/**
 * Resolve `format=` for a column. Three accepted shapes:
 *
 *   format=currency                        every column
 *   format="Revenue:currency, GM:percent"  per column
 *   format="currency, GM:percent"          a default, overridden per column
 *
 * Returns undefined when the attr says nothing about a column, so each caller
 * keeps its own fallback — the name heuristic for charts, "text" for the
 * non-numeric columns of a table. Unrecognised bare words are ignored.
 */
export function parseFormat(
  spec: string | undefined,
): (col: string) => FormatName | undefined {
  const byCol = parseColumnSpec(spec) as Record<string, FormatName>;
  let all: FormatName | undefined;
  for (const part of (spec ?? "").split(",")) {
    const word = part.trim();
    if (word && !word.includes(":") && isFormatName(word)) all = word;
  }
  return (col) => byCol[col] ?? all;
}
